import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';
import DessertFaq from '../faqs/DessertFaq';

function DessertCategory() {
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  const { content: cms } = usePageContent('cuisine_dessert');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Dessert food ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for ice cream, donuts, cookies, shaved ice, or churros? Browse { city_name } dessert { vehicleLowerCase }s, check menus and schedules, or request catering for an event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>Dessert { vehicleLowerCase }s usually specialize: ice cream and frozen custard, donuts and pastries, cookies, shaved ice, churros, or crepes. Many run evenings and weekends rather than lunch, so check the schedule. Dairy-free and gluten-free options vary by { vehicleLowerCase }; the menu will say.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate'>🍦 Seattle’s Most Crave-Worthy Desserts on Wheels</h3>
          <p>A great dessert starts with high-quality ingredients and a passion for sweet flavors. Seattle’s best dessert trucks are run by dedicated bakers and ice cream artisans who believe in making every treat from scratch. <Link to='/food-trucks/goodbelly'>GoodBelly</Link> is one of the city’s favorite dessert trucks, serving warm, sugar-coated malasadas inspired by Hawaiian bakeries. Each one is fried to golden perfection and filled with custard or served plain for those who prefer a more traditional take. <Link to='/food-trucks/fisher-fair-scones'>Fisher Fair Scones</Link> has been a Northwest favorite for over a century, baking fresh scones daily and serving them with rich whipped butter and their signature raspberry jam.</p>
          <p>Seattle’s food trucks don’t just stick to the basics — they specialize in unique, crave-worthy treats that you won’t find anywhere else. <Link to='/food-trucks/menchie-s-frozen-yogurt'>Menchie’s Frozen Yogurt</Link> offers rotating flavors with an exciting selection of toppings, making it the perfect grab-and-go option for a warm day. <Link to='/food-trucks/shug-s-soda-fountain-ice-cream'>Shug’s Soda Fountain & Ice Cream</Link> is a nostalgic favorite, serving all-natural scoops topped with fresh ingredients and seasonal flavors. <Link to='/food-trucks/street-treats'>Street Treats</Link> takes the classic ice cream sandwich to another level, creating handcrafted frozen treats using freshly baked cookies and house-made ice cream in flavors that change with the seasons.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Dessert catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>A dessert { vehicleLowerCase } is an easy add-on to a main caterer, or the whole plan for a shorter event. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>🍓 The Best Seasonal Desserts from Seattle’s Food Trucks</h3>
          <p>Seattle’s food truck desserts change with the seasons, offering flavors that highlight fresh, local ingredients. In the summer, frozen treats take center stage, with <Link to='/food-trucks/shug-s-soda-fountain-ice-cream'>Shug’s Soda Fountain & Ice Cream</Link> serving up refreshing sundaes, <Link to='/food-trucks/menchie-s-frozen-yogurt'>Menchie’s Frozen Yogurt</Link> offering dairy-free sorbets, and <Link to='/food-trucks/street-treats'>Street Treats</Link> crafting ice cream sandwiches with bright, fruity flavors. As fall arrives, the menus shift to warm and comforting flavors, with <Link to='/food-trucks/fisher-fair-scones'>Fisher Fair Scones</Link> baking up spiced pumpkin scones and <Link to='/food-trucks/goodbelly'>GoodBelly</Link> rolling out malasadas filled with cinnamon-sugar custard.</p>
          <p>Winter brings rich, indulgent flavors to the forefront. Hot, buttery scones with raspberry jam from <Link to='/food-trucks/fisher-fair-scones'>Fisher Fair Scones</Link> make for the perfect cold-weather treat, while <Link to='/food-trucks/shug-s-soda-fountain-ice-cream'>Shug’s Soda Fountain & Ice Cream</Link> introduces spiced caramel sundaes topped with crushed ginger cookies. Springtime desserts lean toward light and citrusy flavors, with sugar-coated malasadas featuring bright lemon curd or tropical passionfruit fillings. No matter the time of year, Seattle’s dessert trucks continue to offer innovative, handcrafted sweets that match the season’s best flavors.</p>
        </>
      }
    </>
  );

  return (
    <CategoryPage
      foodName='Dessert'
      foodCategory='dessert'
      seoTitle={cms?.meta?.title || `Dessert food ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book dessert food ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire dessert catering for your next event.`}
      seoCanonical='/dessert-food-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
      Faq={<DessertFaq />}
    />
  );
}

export default DessertCategory;
